import React, { useState, useMemo } from 'react'
import SubscriptionDisplayCard from './SubscriptionDisplayCard'
import AlertSetMemberCard from '../alert/AlertSetMemberCard'
import { isPhoneVerified } from '../alert/AlertSetMemberCard.helpers'
import { useUpdateGroupMembers } from '../user/redux/hooks'
import { Loader } from '../../components'
import classNames from 'classnames'
import './HeatStressDowngradeMemberRemoval.less'

function HeatStressDowngradeMemberRemoval ({
  user,
  userActions,
  subscription,
  currentTier,
  newTier,
  currentMaxMembers,
  newMaxMembers,
  onDowngradeComplete
}) {
  const [selectedForRemoval, setSelectedForRemoval] = useState(new Set())
  const [isUpdating, setIsUpdating] = useState(false)
  const { updateGroupMembers } = useUpdateGroupMembers('heatStressGroupMembers')

  // Get SMS members (members with verified phones)
  const smsMembers = useMemo(() => {
    const members = user?.info?.heatStressGroupMembers || []
    const userObj = { info: user?.info }
    return members.filter(m => !m.admin && m.phone && isPhoneVerified(m, userObj))
  }, [user])

  // Calculate counts
  const members = user?.info?.heatStressGroupMembers || []
  const userObj = { info: user?.info }
  const adminMember = members.find(m => m.admin)
  const adminHasVerifiedPhone = adminMember && isPhoneVerified(adminMember, userObj)
  const currentSmsCount = (adminHasVerifiedPhone ? 1 : 0) + smsMembers.length
  const needToRemove = currentSmsCount - newMaxMembers
  const canProceed = selectedForRemoval.size >= needToRemove

  const toggleMember = (memberId) => {
    setSelectedForRemoval(prev => {
      const next = new Set(prev)
      if (next.has(memberId)) {
        next.delete(memberId)
      } else {
        next.add(memberId)
      }
      return next
    })
  }

  const handleDowngrade = async () => {
    setIsUpdating(true)

    try {
      // Get current members
      const currentMembers = user.info.heatStressGroupMembers || []

      // Filter out selected members
      const updatedMembers = currentMembers.filter(m =>
        !selectedForRemoval.has(m._id)
      )

      // Update via redux (triggers debounced API call)
      updateGroupMembers(updatedMembers)

      // Wait for debounce to fire (500ms + buffer)
      await new Promise(resolve => setTimeout(resolve, 700))

      // Proceed to downgrade
      onDowngradeComplete()
    } catch (error) {
      console.error('Failed to update members:', error)
      setIsUpdating(false)
    }
  }

  return (
    <div className='heat-stress-downgrade-member-removal'>
      <div className='sub-display-card'>
        <SubscriptionDisplayCard subscription={subscription} />
      </div>

      <div className='warning-section'>
        <h3>You'll need to remove {needToRemove} user{needToRemove !== 1 ? 's' : ''} from SMS notifications</h3>
        <p>
          Your new plan supports only {newMaxMembers - 1} SMS user{newMaxMembers - 1 !== 1 ? 's' : ''}.
          Choose which users should no longer receive SMS notifications.
        </p>
      </div>

      <div className='members-grid'>
        {smsMembers.map(member => (
          <div
            key={member._id}
            className={classNames('member-card-wrapper', {
              selected: selectedForRemoval.has(member._id)
            })}
            onClick={() => toggleMember(member._id)}
          >
            <div className='remove-badge'>
              {selectedForRemoval.has(member._id) && (
                <span className='x-icon'>✕</span>
              )}
            </div>
            <AlertSetMemberCard
              member={member}
              showAlertSets={false}
              showUtilityMenu={false}
              userActionTypePrefix='heat-stress-group-member'
            />
          </div>
        ))}
      </div>

      <div className='actions'>
        {!canProceed && (
          <p className='helper-text'>
            Select {needToRemove - selectedForRemoval.size} more member{(needToRemove - selectedForRemoval.size) !== 1 ? 's' : ''} to remove
          </p>
        )}
        <button
          className='btn btn-primary btn-large'
          disabled={!canProceed || isUpdating}
          onClick={handleDowngrade}
        >
          {isUpdating ? <Loader /> : 'Confirm'}
        </button>
      </div>
    </div>
  )
}

export default HeatStressDowngradeMemberRemoval

HeatStressDowngradeMemberRemoval.displayName = 'HeatStressDowngradeMemberRemoval'
